import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, inject } from '@angular/core';
import { ModalDialogService } from '@ddv/common-components';
import { DashboardFiltersService } from '@ddv/dashboards';
import {
    DataConfigSummary,
    FieldMetadata,
    MetadataFormatUtils,
    SummaryColumnConfig,
    SummaryColumnFilter,
    SummaryConfig,
    Summary,
    ValueFilterOption,
    MANAGE_WIDGET_ID,
} from '@ddv/models';
import { clone } from '@ddv/utils';

import { DropZoneConfig } from '../../../models/drop-zone-config';
import { ColumnFormattingSummaryComponent } from '../column-formatting-summary/column-formatting-summary.component';
import { ConfigureComponent } from '../configure/configure.component';
import * as dropZoneConfig from '../drop-zone-config';

@Component({
    selector: 'app-mw-summary-configure',
    templateUrl: './configure-summary.component.html',
    styleUrls: ['../configure/configure.component.scss'],
    standalone: false,
})
export class ConfigureSummaryComponent extends ConfigureComponent {
    private readonly modalService = inject(ModalDialogService);
    private readonly dashboardFiltersService = inject(DashboardFiltersService);

    readonly maxSummaries = 10;
    override dataConfigs: { summary: DataConfigSummary, filters: SummaryColumnFilter[] }[] = [];
    filterValues: ValueFilterOption[] = [];
    filterSearchValue: string | undefined;

    constructor() {
        super();

        this.initDashboardFilter();
    }

    override initDataConfigs(): void {
        const isRendered = this.hasRequiredFields();
        if (this.dataConfigs.length < this.maxSummaries) {
            this.dataConfigs.push({ summary: { isEmpty: true }, filters: [] });
            if (isRendered) {
                this.renderPreview();
            }
        }
    }

    getDropZones(): DropZoneConfig[] {
        return dropZoneConfig.summary;
    }

    showColumnFormatters(summaryConfig: DataConfigSummary): void {
        const dialogRef = this.modalService
            .open<SummaryColumnConfig, ColumnFormattingSummaryComponent>(ColumnFormattingSummaryComponent, { windowClass: 'column-formatter-popup' });
        dialogRef.componentInstance.parentData = {
            isAttribute: summaryConfig.isAttribute,
            columnName: summaryConfig.summaryLabel,
            displayType: summaryConfig.displayType,
            columnConfiguration: summaryConfig.summaryWidgetColumnConfiguration,
        };
        dialogRef.result.then(
            (response) => {
                if (response && response !== 'cancel') {
                    if (response.aggregationType === 'count' || response.aggregationType === 'countDistinct' ||
                        response.aggregationType === 'countExcludingZeros' || response.aggregationType === 'countDistinctExcludingZeros') {
                        response.numberFormat = 'XX';
                    }
                    summaryConfig.summaryWidgetColumnConfiguration = response;
                    this.renderPreview();
                }
            },
            (dismissData) => dismissData);
    }

    toggleFilterValues(filterValuesList: HTMLElement, summaryColFilter: SummaryColumnFilter, configIndex: number, index: number): void {
        if (filterValuesList.hidden) {
            this.showFiltersValues(filterValuesList, summaryColFilter.filterField, configIndex, index);
        } else {
            this.hideFiltersValues(filterValuesList, configIndex, index);
        }
    }

    showFiltersValues(filterValuesList: HTMLElement, attribute: string, configIndex: number, index: number): void {
        filterValuesList.hidden = false;
        this.filterValues = this.dashboardFiltersService.getValueOptionsByAttribute(attribute);
        const summaryWidgetFilterValues = this.dataConfigs[configIndex].filters[index].summaryWidgetFilterValues;

        summaryWidgetFilterValues.forEach((summaryWidgetFilterValue) => {
            const summaryValue = summaryWidgetFilterValue.filterValue == null ? '' : summaryWidgetFilterValue.filterValue.toString();
            const summaryFilterValue = this.filterValues.find((filterWidgetValue) => {
                const filterValue = filterWidgetValue.value == null ? '' : filterWidgetValue.value.toString();

                return filterValue.toLocaleLowerCase() === summaryValue.toLocaleLowerCase();
            });

            if (summaryFilterValue) {
                summaryFilterValue.checked = true;
            } else {
                this.insertIntoFilterValues(summaryWidgetFilterValue.filterValue);
            }
        });
    }

    hideFiltersValues(filterValuesList: HTMLElement, configIndex: number, index: number): void {
        if (filterValuesList.hidden) {
            return;
        }
        filterValuesList.hidden = true;
        const colFilterConfig = this.dataConfigs[configIndex].filters;
        colFilterConfig[index].summaryWidgetFilterValues = [];
        this.filterValues.forEach((filterValue) => {
            if (filterValue.checked) {
                colFilterConfig[index].summaryWidgetFilterValues.push({ filterValue: filterValue.value });
            }
        });
        this.renderPreview();
    }

    uncheckFilterValues(): void {
        this.filterValues.forEach((filterValue) => filterValue.checked = false);
    }

    addToFilterValues(): void {
        if (this.filterSearchValue?.trim() &&
            !this.filterValues.some((filterValue) => filterValue.value.toLowerCase() === this.filterSearchValue?.toLowerCase())) {
            this.insertIntoFilterValues(this.filterSearchValue);
        }
        this.filterSearchValue = '';
    }

    dragStarted(metadata: FieldMetadata): void {
        this.dropZones.forEach((item) => {
            item.allowed = !!((item.configKey === 'summary') ?
                this.dataConfigs.some((config) => config.summary.isEmpty) :
                metadata.isAttribute);
        });
    }

    onCdkDropSuccess(
        data: CdkDragDrop<{ summary: DataConfigSummary, filters: SummaryColumnFilter[] }, unknown>,
        zone: DropZoneConfig,
        index: number,
    ): void {
        if (!zone.allowed) {
            return;
        }
        let isDropSuccess = false;
        if (zone.configKey === 'summary' && this.dataConfigs[index].summary.isEmpty) {
            const isDate = data.item.data.datatype === 'date';
            this.dataConfigs[index].summary = {
                isAttribute: data.item.data.isAttribute,
                summaryLabel: data.item.data.displayName,
                summaryField: data.item.data.name,
                displayType: data.item.data.displayType,
                summaryWidgetColumnConfiguration: clone(MetadataFormatUtils
                    .getDefaultSummaryColFormatter(data.item.data.isAttribute, isDate)),
                isEmpty: false,
            };
            isDropSuccess = true;
        } else if (zone.configKey === 'filters' &&
            !this.dataConfigs[index].filters.some((dataConfig) => dataConfig.filterField === data.item.data.name)) {
            this.dataConfigs[index].filters.push({
                filterLabel: data.item.data.displayName,
                filterField: data.item.data.name,
                summaryWidgetFilterValues: [],
            });
            isDropSuccess = true;
        }
        if (isDropSuccess) {
            this.renderPreview();
        }
        this.activelyDragging = false;
    }

    removeConfigItemAtIndex(configKey: 'summary' | 'filters', configIndex: number, index?: number): void {
        const summaryConfig = this.dataConfigs[configIndex];
        if (configKey === 'filters' && index !== undefined) {
            summaryConfig.filters.splice(index, 1);
        } else {
            summaryConfig.summary = { isEmpty: true };
        }
        if (this.dataConfigs.length > 1 && summaryConfig.summary.isEmpty && !summaryConfig.filters.length) {
            this.dataConfigs.splice(configIndex, 1);
        }
        this.renderPreview();
    }

    override getSelectedConfigs(): Partial<SummaryConfig> {
        return {
            summaries: this.dataConfigs.reduce((sums: Summary[], item) => {
                sums.push({
                    summaryField: item.summary.summaryField ?? '',
                    summaryLabel: item.summary.summaryLabel ?? '',
                    summaryWidgetColumnConfiguration: item.summary.summaryWidgetColumnConfiguration as SummaryColumnConfig,
                    summaryWidgetFilters: item.filters.map((filter) => ({
                        filterField: filter.filterField,
                        filterLabel: filter.filterLabel,
                        summaryWidgetFilterValues: filter.summaryWidgetFilterValues,
                    })),
                });
                return sums;
            }, []),
        };
    }

    /** For Edit/Find Widget flow ***/
    setDataConfigs(summaryConfig: SummaryConfig): void {
        summaryConfig.summaries.forEach((summary) => {
            this.dataConfigs.push({
                summary: {
                    summaryLabel: summary.summaryLabel,
                    summaryField: summary.summaryField,
                    summaryWidgetColumnConfiguration: summary.summaryWidgetColumnConfiguration,
                    isEmpty: false,
                },
                filters: summary.summaryWidgetFilters.map((summaryWidgetFilter) => ({
                    filterLabel: summaryWidgetFilter.filterLabel,
                    filterField: summaryWidgetFilter.filterField,
                    summaryWidgetFilterValues: summaryWidgetFilter.summaryWidgetFilterValues,
                })),
            });
        });
    }

    override hasRequiredFields(): boolean {
        return !this.dataConfigs.some((dataConfig) => !dataConfig.filters.length || dataConfig.summary.isEmpty);
    }

    afterMetadataUpdate(): void {
        this.dataConfigs.forEach((dataConfig) => {
            const metaDatum = this.metadata.find((metadatum) => metadatum.name === dataConfig.summary.summaryField);
            dataConfig.summary.displayType = metaDatum?.displayType;
            dataConfig.summary.isAttribute = metaDatum?.isAttribute;
        });
    }

    getFilterChecked(filter: ValueFilterOption): boolean {
        return !!filter.checked;
    }

    setFilterChecked(event: boolean, filter: ValueFilterOption): void {
        filter.checked = event;
    }

    private initDashboardFilter(): void {
        this.dashboardFiltersService.setFilterType('coreWidget');
        this.dashboardFiltersService.setSlicerValues(MANAGE_WIDGET_ID);
    }

    private insertIntoFilterValues(filterValue: string): void {
        this.filterValues.unshift({
            value: filterValue,
            text: filterValue,
            checked: true,
        });
    }
}
